import * as React from 'react';
import { createPortal } from 'react-dom';

export type TooltipPlacementProps =
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'middle';

type TooltipProps = {
    children?: React.ReactNode;
    title?: any;
    placement?: TooltipPlacementProps;
};

const Tooltip = ({
    children,
    title,
    placement = 'top',
}: TooltipProps): React.JSX.Element => {
    const [isTooltipVisible, setTooltipVisible] = React.useState(false);
    const [tooltipPosition, setTooltipPosition] = React.useState({
        top: 0,
        left: 0,
    });
    const triggerRef = React.useRef<HTMLDivElement>(null);
    const tooltipRef = React.useRef<HTMLDivElement>(null);

    // Show/hide tooltip with correct positioning
    const handleMouseEnter = (): void => {
        if (title && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const tooltipWidth = tooltipRef.current?.offsetWidth || 0;
            const tooltipHeight = tooltipRef.current?.offsetHeight || 0;

            const positions = {
                top: {
                    top: rect.top - tooltipHeight - 30,
                    left: rect.left + rect.width / 2 - tooltipWidth / 2,
                },
                bottom: {
                    top: rect.bottom + 8,
                    left: rect.left + rect.width / 2 - tooltipWidth / 2,
                },
                left: {
                    top: rect.top + rect.height / 2 - tooltipHeight / 2,
                    left: rect.left - tooltipWidth - 8,
                },
                right: {
                    top: rect.top + rect.height / 2 - tooltipHeight / 2,
                    left: rect.right + 8,
                },
                middle: {
                    top: rect.top + rect.height / 2 - tooltipHeight / 2,
                    left: rect.left + rect.width / 2 - tooltipWidth / 2,
                },
            };

            setTooltipPosition(positions[placement] || positions.top);
            setTooltipVisible(true);
        }
    };

    const handleMouseLeave = (): void => {
        setTooltipVisible(false);
    };

    return (
        <>
            <div
                ref={triggerRef}
                className="relative inline-block"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children}
            </div>

            {isTooltipVisible &&
                createPortal(
                    <div
                        ref={tooltipRef}
                        className={`fixed z-[9999]  break-words bg-gray-600 text-white py-1 px-2 text-xs font-medium rounded shadow-sm text-center whitespace-nowrap transition-opacity duration-300 ${
                            isTooltipVisible
                                ? 'opacity-100 visible'
                                : 'opacity-0 invisible'
                        }`}
                        style={{
                            top: `${tooltipPosition.top}px`,
                            left: `${tooltipPosition.left}px`,
                            zoom: '125%',
                            overflowWrap: 'break-word',
                        }}
                    >
                        {title}
                        <div className="tooltip-arrow" data-popper-arrow></div>
                    </div>,
                    document.body
                )}
        </>
    );
};

export default Tooltip;
